import {
  alpha,
  Autocomplete,
  Box,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React, { FC, useState } from 'react';
import { ScrollBarProps } from 'react-perfect-scrollbar';
import useLog from 'src/hooks/useLog';
import ChevronDown from 'src/icons/ChevronDown';
import ChevronUp from 'src/icons/ChevronUp';
import Download from 'src/icons/Download';
import Search from 'src/icons/Search';
import { generateCsv } from 'src/utils/generateCsv';
import { Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import CopyToClipboardButton from '../CopyToClipboardButton';
import LoadingScreen from '../LoadingScreen';
import Scrollbar from '../Scrollbar';
import TetherButton from '../TetherButton';
import TetherTableColumn, { DisplayType, getColumnLabel } from './TetherTableColumn';

/**
 * Page NUMBERS in the footer, not just a next arrow.
 *
 * MUI's default `TablePagination` actions are first/prev/next/last, which is enough to
 * walk a table and useless for reaching page 40 of 444. A device list of 8,880 rows is
 * exactly the case where someone wants to jump, and it is the case where walking is
 * worst.
 *
 * `count === -1` means the total is genuinely unknown — the server answered with an array
 * rather than an envelope — and then there is no page count to number. Arrows are the
 * honest control for that, so the numbers disappear rather than being invented.
 */
const TetherTablePaginationActions: FC<TablePaginationActionsProps & { isLastPage?: boolean }> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  isLastPage = false,
}) => {
  if (count < 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
        <IconButton
          onClick={(event) => onPageChange(event, page - 1)}
          disabled={page === 0}
          aria-label="previous page"
          size="small"
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        {/*
          Without a total the only signal that the list has ended is a SHORT page, and
          without honouring it the next arrow never stops: an operator walks off the end
          into an empty table under an inverted "61-60 of 60" footer, with nothing saying
          they have gone past it.
        */}
        <IconButton
          onClick={(event) => onPageChange(event, page + 1)}
          disabled={isLastPage}
          aria-label="next page"
          size="small"
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>
    );
  }

  const pageCount = Math.max(1, Math.ceil(count / rowsPerPage));

  return (
    <Pagination
      sx={{
        ml: 2,
        // The page numbers are a single row, always. MUI's Pagination lays its items out
        // in a wrapping flex list, and inside TablePagination's toolbar — which also
        // holds the rows-per-page select and the "1-50 of 8,880" label — there is
        // routinely not enough width, so the numbers fell onto a second line and the
        // footer changed height as you paged. Scrolling the toolbar is the correct
        // degradation for a genuinely narrow viewport; re-flowing the control is not.
        '& .MuiPagination-ul': { flexWrap: 'nowrap' },
      }}
      // MUI's Pagination is 1-based and TablePagination is 0-based; the conversion lives
      // here so no caller has to know that two MUI components disagree.
      count={pageCount}
      page={page + 1}
      onChange={(event, nextPage) => onPageChange(event as React.MouseEvent<HTMLButtonElement>, nextPage - 1)}
      siblingCount={1}
      boundaryCount={1}
      showFirstButton
      showLastButton
      size="small"
      color="primary"
    />
  );
};

/**
 * Server-side paging, searching and sorting.
 *
 * When this is supplied the table STOPS filtering, sorting and slicing `data` in memory:
 * `data` is the page the server returned, and every control reports its change back to
 * the caller instead of acting locally.
 *
 * It exists because paging the transport while the caller still loads every row defeats
 * the point — the browser holds the whole table either way. With this, a 114,000-row
 * device list is twenty rows on the wire and a search is a WHERE clause.
 */
export interface TetherTableServerPaging {
  /** 0-based, to match MUI's TablePagination. */
  page: number;
  rowsPerPage: number;
  /** Changes with the result-set subject so stale selections are cleared. */
  scope?: string | number;
  /** Total rows matching the current search on the SERVER, not the length of `data`. */
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  /**
   * The page sizes to offer, when the default [20, 50, 100, 200] is too generous.
   *
   * A page size is not always just a number of rows: where the caller resolves something
   * PER ROW after the page lands, it is also a request count. The orders cards cap
   * themselves for exactly that reason — see CUSTOMER_ORDER_TABLE_PAGING.
   */
  rowsPerPageOptions?: number[];
  /** The current search term. The caller owns debouncing — this fires on every keystroke. */
  searchQuery: string;
  onSearchChange: (query: string) => void;
  /**
   * Column sorting, when the server can do it. Omitted means the headers are not
   * clickable: sorting one page of a larger result in the browser reorders twenty rows
   * and calls it a sort, which is worse than not offering it.
   */
  sort?: {
    /** The column's `attribute`, as the API names it. */
    field?: string;
    desc: boolean;
    onSortChange: (field: string, desc: boolean) => void;
    /** Attributes the API accepts; anything else stays unsortable. */
    sortableFields: readonly string[];
  };
}

interface TetherTableProps<T> extends ScrollBarProps {
  loading?: boolean;
  data: T[];
  /** Opt into server-side paging/search/sort. See {@link TetherTableServerPaging}. */
  serverPaging?: TetherTableServerPaging;
  searchable?: boolean;
  miniSearch?: boolean;
  columns: TetherTableColumn<T>[];
  rowRenderKeyAttribute: keyof T;
  tableId?: string; // If using mutliple tables for potentially clashing data on the same page, add a unique ID
  onRowClick?: (displayObject: T) => void;
  getRowNavigationRoute?: (displayObject: T) => string;
  rowActions?: (displayObject: T) => JSX.Element;
  headerActions?: JSX.Element;
  disablePagination?: boolean;
  emptyText?: string;
  enableColumnSelection?: boolean;
  enableColumnGroups?: boolean;
  stickyHeader?: boolean;
  enableCSVExport?: boolean;
  customBulkActions?: (selectedObjects: T[]) => JSX.Element;
  rowBackgroundColor?: (displayObject: T) => string;
  initialSortColumnIndex?: number;
  initialSortDirection?: 'asc' | 'desc';
  disableRowHover?: boolean;
  expandableContent?: (row: T) => React.ReactNode;
}

function TetherTable<T>(props: TetherTableProps<T>) {
  const {
    loading,
    data,
    serverPaging,
    searchable,
    miniSearch,
    columns: initialColumns,
    rowRenderKeyAttribute,
    tableId = '',
    onRowClick,
    getRowNavigationRoute,
    rowActions,
    disablePagination,
    emptyText,
    enableColumnSelection,
    enableColumnGroups,
    stickyHeader,
    enableCSVExport,
    customBulkActions,
    rowBackgroundColor,
    initialSortColumnIndex,
    initialSortDirection,
    disableRowHover = false,
    expandableContent,
    ...other
  } = props;

  const columns = initialColumns.filter((c) => !c.disabled);

  const [sortColumnIndex, setSortColumnIndex] = useState<number>(initialSortColumnIndex || -1);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection || 'asc');
  const [query, setQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(disablePagination ? Number.MAX_SAFE_INTEGER : 20);
  const [visibleColumns, setVisibleColumns] = useState<number[]>(
    columns.map((_, index) => index).filter((index) => !columns[index].hideByDefault)
  );
  const [selectedRows, setSelectedRows] = useState<T[]>([]);

  // A server page swap replaces every row object, so a selection carried across it is a
  // selection of rows nobody can see. Cleared rather than silently shrunk.
  //
  // The SORT and SCOPE are part of that key: either can re-query and return a wholly new
  // set of rows while page, rowsPerPage and searchQuery are unchanged. With
  // keepPreviousData, omitting the scope left the previous device type's selected objects
  // live in bulk actions while those previous rows remained on screen.
  const serverPageKey = serverPaging
    ? `${serverPaging.page}:${serverPaging.rowsPerPage}:${serverPaging.searchQuery}` +
      `:${serverPaging.sort?.field ?? ''}:${serverPaging.sort?.desc ?? ''}:${serverPaging.scope ?? ''}`
    : '';
  const lastServerPageKey = React.useRef(serverPageKey);
  if (lastServerPageKey.current !== serverPageKey) {
    lastServerPageKey.current = serverPageKey;
    if (selectedRows.length > 0) {
      setSelectedRows([]);
    }
  }
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleExpandByKey = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleExpand = (key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleExpandByKey(key);
  };

  const theme = useTheme();
  const log = useLog();

  const bulkActionsEnabled = enableCSVExport || !!customBulkActions;

  /**
   * The API field a column sorts by in SERVER mode, or undefined if it cannot be sorted.
   *
   * `sortField` first, because `attribute` is a render key that several columns share —
   * three headers on the device list declare `attribute: 'id'` and would otherwise all
   * re-query sorted by device UUID while claiming to sort by customer, type or devEUI.
   */
  const serverSortField = (column?: TetherTableColumn<T>): string | undefined => {
    if (!serverPaging?.sort || !column) {
      return undefined;
    }
    // No fallback to `attribute`. It is a RENDER key, not a sort field, and several
    // columns legitimately share one: three device headers render from the row's id via
    // getValue, so falling back made "Assigned To Customer", "Sigfox/DevEUI" and "ID" all
    // resolve to 'id' — clicking one re-queried by device UUID, and once sorted every one
    // of them painted a chevron, so the table asserted three different sort orders at
    // once. A column is server-sortable only if it SAYS which field it sorts by.
    const field = column.sortField;
    return field && serverPaging.sort.sortableFields.includes(field) ? field : undefined;
  };

  const updateSort = (index: number) => {
    // In server mode the sort has to be the SERVER's: reordering the twenty rows that
    // happen to be on this page and presenting it as a sorted table is a wrong answer, so
    // a column the API cannot sort by simply does not respond to a click.
    if (serverPaging) {
      const field = serverSortField(columns[index]);
      if (!field) {
        return;
      }
      const desc = serverPaging.sort!.field === field ? !serverPaging.sort!.desc : false;
      serverPaging.sort!.onSortChange(field, desc);
      return;
    }

    if (sortColumnIndex !== index) {
      setSortColumnIndex(index);
      setSortDirection('asc');
    } else {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };

  let fiteredData = data;

  const getValue = (column: TetherTableColumn<T>, object: T) => {
    if (column.getValue) {
      return column.getValue(object);
    }

    return object[column.attribute];
  };

  const getValueAsString = (column: TetherTableColumn<T>, object: T): string => {
    const value = getValue(column, object);
    if (value == undefined) return '';
    return value.toString();
  };

  const getFormattedValue = (column: TetherTableColumn<T>, object: T) => {
    const value = getValue(column, object);

    switch (column.displayType) {
      case DisplayType.CURRENCY:
        return `$${parseFloat(`${value}`).toFixed(2)}`;
      case DisplayType.DATE:
        return value ? format(new Date(value as any), 'dd/MM/yyyy') : '';
      case DisplayType.DATETIME:
        return value ? format(new Date(value as any), 'dd/MM/yyyy - HH:mm') : '';
      case DisplayType.JSON:
        return JSON.stringify(value, null, 2);
      default:
        return value;
    }
  };

  const getSubtextValue = (column: TetherTableColumn<T>, object: T) => {
    if (column.getSubtextValue) {
      return column.getSubtextValue(object);
    }

    return object[column.subTextAttribute];
  };

  // Neither the sort nor the filter below runs in SERVER mode. `data` is the page the API
  // already filtered, sorted and windowed, and both of these operate on it in place —
  // `fiteredData` starts as `data` itself, so `.sort()` mutates react-query's cached page.
  // A header clicked before the table switched into server mode (client mode leaves
  // `sortColumnIndex` set) then re-ordered every server page underneath the server's own
  // chevron, and the row objects the cache holds came back reordered to the next reader.
  if (!serverPaging && sortColumnIndex >= 0) {
    const sortColumn = columns[sortColumnIndex < 0 || sortColumnIndex >= columns.length ? 0 : sortColumnIndex];
    fiteredData = [...fiteredData].sort((a, b) => {
      let result = 0;
      if (sortColumn.sort) {
        result = sortColumn.sort(a, b);
      } else {
        let compA: any = getValue(sortColumn, a);
        let compB: any = getValue(sortColumn, b);

        // Convert dates to milliseconds for sorting
        if (sortColumn.displayType && sortColumn.displayType.startsWith('date')) {
          compA = compA ? new Date(compA).getTime() : Number.MAX_SAFE_INTEGER;
          compB = compB ? new Date(compB).getTime() : Number.MAX_SAFE_INTEGER;
        }

        compA = typeof compA === 'string' ? compA.toLowerCase() : compA;
        compB = typeof compB === 'string' ? compB.toLowerCase() : compB;

        if (compA < compB) {
          result = -1;
        } else if (compB < compA) {
          result = 1;
        } else {
          result = 0;
        }
      }

      if (sortDirection === 'desc') {
        return 0 - result;
      }

      return result;
    });
  }

  fiteredData = serverPaging ? fiteredData : fiteredData.filter((object) => {
    const columnValues = columns.reduce(
      (acc, column) => acc + getValue(column, object) + (column.subTextAttribute ? getSubtextValue(column, object) : ''),
      ''
    );
    const queries = query.split('+').filter((q) => !!q.trim());

    return (
      queries.length === 0 ||
      !!queries.find((q) => {
        const normalisedColumn = columnValues
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
        const normalisedSearch = q
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
        return normalisedColumn.includes(normalisedSearch);
      })
    );
  });

  // In server mode `data` IS the page: it has already been filtered, sorted and windowed
  // by the API, so re-doing any of that here would reorder and re-slice one page and
  // present the result as if it were the whole table.
  const displayObjects = serverPaging ? data : fiteredData.slice(page * limit, page * limit + limit);

  /** Nothing to show yet — the first load, or a reload that has no previous rows. */
  const showEmptyLoadingState = Boolean(loading) && displayObjects.length === 0;
  /** Rows on screen, a request in flight: keep them, dim them, say so. */
  const refreshing = Boolean(loading) && displayObjects.length > 0;

  const isColumnVisible = (column: TetherTableColumn<T> | number) =>
    visibleColumns.includes(typeof column === 'number' ? column : columns.indexOf(column));

  const columnGroups = [];
  if (enableColumnGroups) {
    // Make header color solid to make scroll behind work but body with opacity to allow cursor highlight to show through
    const colors = [alpha(theme.palette.grey[100], 0.5), alpha(theme.palette.grey[200], 0.5)];
    const headerColors = [alpha(theme.palette.grey[50], 1), alpha(theme.palette.grey[100], 1)];
    let lastColor = colors[1];
    let lastHeaderColor = headerColors[1];

    columns.forEach((column) => {
      if (isColumnVisible(column)) {
        if (columnGroups.length > 0 && columnGroups[columnGroups.length - 1].name === column.groupName) {
          columnGroups[columnGroups.length - 1].count++;
        } else {
          lastColor = lastColor === colors[0] ? colors[1] : colors[0]; // alternating grey backgrounds
          lastHeaderColor = lastHeaderColor === headerColors[0] ? headerColors[1] : headerColors[0];
          columnGroups.push({
            name: column.groupName,
            count: 1,
            color: column.groupName ? lastColor : 'none', // for ungrouped columns leave background empty
            headerColor: column.groupName ? lastHeaderColor : 'none',
          });
        }
      }
    });
  }

  const getColumnGroup = (column: TetherTableColumn<T> | number) => {
    const index = typeof column === 'number' ? column : columns.filter((c) => isColumnVisible(c)).indexOf(column);
    let total = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const col of columnGroups) {
      total += col.count;
      if (index < total) {
        return col;
      }
    }

    // Fallback to an empty group
    return {};
  };

  // When the displayed selection changes the selected rows are not changed to allow retaining selection
  // while modifying displayed data subset, so filter by what's currently displayed
  const visibleSelectedRows = selectedRows.filter((r) => fiteredData.includes(r));

  /**
   * What a selection can act on, said in the words that are true.
   *
   * In server mode `data` is ONE page, so ticking the header checkbox selects the rows on
   * screen — not the 114,000 the footer counts. Worse, `visibleSelectedRows` keeps only
   * rows still present in `fiteredData`, and paging replaces those objects wholesale, so
   * rows ticked on page 1 were silently dropped from the action the moment page 2 arrived
   * and the button reported a smaller number than the operator had ticked.
   *
   * The selection is therefore cleared whenever the page changes, and every count says
   * "on this page" — an action scoped to what you can see is defensible; one that claims a
   * scope it does not have is not.
   */
  const selectionScope = serverPaging ? 'on this page' : 'items';
  const selectionSummary = `${visibleSelectedRows.length} of ${fiteredData.length} ${
    serverPaging ? `selected ${selectionScope}` : 'items selected'
  } `;

  return (
    <Box className="tether-table">
      {/* Search & column selection header */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        {searchable && (
          <Box
            sx={{
              m: 1,
              minWidth: miniSearch ? undefined : '30%',
              flex: miniSearch ? undefined : 1,
            }}
          >
            <TextField
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => (serverPaging ? serverPaging.onSearchChange(e.target.value) : setQuery(e.target.value))}
              placeholder={serverPaging ? 'Search' : "Search (use '+' to search multiple terms at once)"}
              value={serverPaging ? serverPaging.searchQuery : query}
              variant="outlined"
            />
          </Box>
        )}
        {enableColumnSelection && (
          <Autocomplete
            sx={{
              m: 1,
              minWidth: '48%',
              flex: 1,
            }}
            multiple
            limitTags={2}
            options={columns.map((_, index) => index)}
            getOptionLabel={(column) => getColumnLabel(columns[column])}
            value={visibleColumns}
            disableCloseOnSelect
            onChange={(_, selected) => {
              const items = selected.filter((s) => typeof s !== 'string') as number[];
              setVisibleColumns(items);
            }}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Show/Hide Columns" />}
          />
        )}
        {bulkActionsEnabled && (
          <Box
            sx={{
              m: 1,
              minWidth: '48%',
              flex: 1,
              position: 'relative',
            }}
          >
            <TextField
              variant="outlined"
              label="Bulk Actions"
              disabled
              multiline
              value=" "
              sx={{
                position: 'absolute',
                height: '100%',
                width: '100%',
                '*': {
                  cursor: 'default',
                  // height: '100%',
                },
                div: {
                  display: 'block',
                  height: '100%',
                },
                label: { color: theme.palette.text.secondary },
              }}
            />
            <Box
              sx={{
                pt: '10px',
                pb: 1,
                pr: 1,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" sx={{ pr: 1 }}>
                {selectionSummary}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  rowGap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                <TetherButton
                  variant="contained"
                  startIcon={<Download fontSize="small" />}
                  disabled={!visibleSelectedRows.length}
                  onClick={() => {
                    generateCsv(
                      visibleSelectedRows,
                      columns.map((c) => c.attribute as string),
                      'report-export.csv'
                    );
                  }}
                >
                  {serverPaging ? `Download ${visibleSelectedRows.length} selected (this page)` : selectionSummary}
                </TetherButton>
                <Box
                  sx={{
                    display: 'flex',
                    rowGap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                  }}
                >
                  <TetherButton
                    variant="contained"
                    startIcon={<Download fontSize="small" />}
                    disabled={!visibleSelectedRows.length}
                    onClick={() => {
                      const rows = visibleSelectedRows.map((r) => {
                        const result = {};
                        Object.assign(
                          result,
                          ...columns.map((c) => ({
                            [c.label || c.attribute]: getValue(c, r),
                          }))
                        );
                        return result;
                      });
                      generateCsv(
                        rows,
                        columns.map((c) => (c.label || c.attribute) as string),
                        'report-export.csv'
                      );
                    }}
                  >
                    Download as CSV
                  </TetherButton>
                  {customBulkActions && customBulkActions(visibleSelectedRows)}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {/*
        A request is in flight and the rows below are the PREVIOUS page. Reserved rather
        than conditional so the table does not shift by 4px every time a page loads.
      */}
      <Box sx={{ height: 4 }}>{refreshing && <LinearProgress aria-label="Loading table data" />}</Box>
      <Scrollbar {...other}>
        {/* Data table */}
        <TableContainer
          sx={{
            maxHeight: '80%',
            width: '100%',
            opacity: refreshing ? 0.5 : 1,
            transition: 'opacity 150ms',
            // The rows on screen are stale mid-fetch; clicking one navigates to a row the
            // user is no longer looking at once the new page lands.
            pointerEvents: refreshing ? 'none' : undefined,
          }}
        >
          <Table stickyHeader={stickyHeader} sx={{ width: '100%' }}>
            <TableHead
              sx={{
                '.MuiTableCell-stickyHeader': {
                  background: 'none',
                },
                borderBottomColor: 'black',
              }}
            >
              {/* Column Group Headers */}
              {enableColumnGroups && (
                <TableRow>
                  {bulkActionsEnabled && <TableCell />}
                  {columnGroups.map((group, i) => (
                    <TableCell
                      key={i}
                      colSpan={group.count}
                      sx={{
                        '&.MuiTableCell-stickyHeader': {
                          position: 'relative',
                          background: group.headerColor,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        {group.name}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              )}

              {/* Column Headers */}
              <TableRow>
                {expandableContent && <TableCell sx={{ width: 40 }} />}
                {bulkActionsEnabled && (
                  <TableCell
                    sx={{
                      '&.MuiTableCell-stickyHeader': {
                        background: theme.palette.background.paper,
                      },
                    }}
                  >
                    <Checkbox
                      checked={!fiteredData.find((o) => !selectedRows.includes(o))}
                      onChange={(_, checked) => {
                        if (checked) {
                          setSelectedRows([...new Set([...selectedRows, ...fiteredData])]);
                        } else {
                          setSelectedRows(selectedRows.filter((r) => !fiteredData.includes(r)));
                        }
                      }}
                    />
                  </TableCell>
                )}
                {columns.map((column, index) => {
                  if (!isColumnVisible(column)) {
                    return null;
                  }

                  const columnLabel = getColumnLabel(column);

                  // In server mode a header is clickable only if the API can sort by it.
                  // Otherwise it kept its pointer cursor and hover underline while doing
                  // nothing — an affordance advertising an action that silently no-ops.
                  const sortField = serverSortField(column);
                  const sortable = serverPaging
                    ? Boolean(sortField)
                    : column.sortable === undefined || column.sortable;

                  // The indicator comes from the SERVER's sort in server mode:
                  // sortColumnIndex is never set there, so the table showed no sign of the
                  // order it was actually in, before OR after a click.
                  const sortedAscending = serverPaging
                    ? sortField !== undefined && serverPaging.sort?.field === sortField && !serverPaging.sort?.desc
                    : index === sortColumnIndex && sortDirection === 'asc';
                  const sortedDescending = serverPaging
                    ? sortField !== undefined && serverPaging.sort?.field === sortField && Boolean(serverPaging.sort?.desc)
                    : index === sortColumnIndex && sortDirection === 'desc';

                  if (sortable) {
                    return (
                      <TableCell
                        key={index}
                        onClick={() => updateSort(index)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          '&.MuiTableCell-stickyHeader': {
                            background: getColumnGroup(column).headerColor,
                            left: 'auto',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="body2" color="textPrimary">
                            {columnLabel}
                          </Typography>
                          {sortedAscending && <ChevronUp color="action" />}
                          {sortedDescending && <ChevronDown color="action" />}
                        </Box>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell key={index}>
                      <Typography variant="body2" color="textPrimary">
                        {columnLabel}
                      </Typography>
                    </TableCell>
                  );
                })}
                {rowActions && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {/*
                Rows stay on screen while the next page loads.
                Blanking the body on every page change, row-size change and settled
                keystroke made the table jump and the footer flicker, and it hid what the
                user was comparing against. `refreshing` dims the stale rows and the
                progress bar above says a request is in flight; the empty-body spinner is
                kept for the case where there is genuinely nothing to show yet.
              */}
              {!showEmptyLoadingState &&
                displayObjects.map((displayObject) => {
                  const currentDisplayObject = displayObject;
                  const rowKey = String(displayObject[rowRenderKeyAttribute]);
                  const shouldExpandOnRowClick = !!expandableContent && !onRowClick && !getRowNavigationRoute;

                  return (
                    <React.Fragment key={`${rowKey} ${tableId}`}>
                      <TableRow
                        component={getRowNavigationRoute ? RouterLink : null}
                        to={getRowNavigationRoute ? getRowNavigationRoute(displayObject) : null}
                        onClick={() => {
                          if (onRowClick) {
                            onRowClick(displayObject);
                            return;
                          }

                          if (shouldExpandOnRowClick) {
                            toggleExpandByKey(rowKey);
                          }
                        }}
                        sx={{
                          cursor: onRowClick || getRowNavigationRoute ? 'pointer' : 'default',
                          backgroundColor: rowBackgroundColor ? rowBackgroundColor(currentDisplayObject) : 'none',
                          '&:hover': {
                            backgroundColor: disableRowHover ? 'none' : alpha(theme.palette.primary.main, 0.05),
                          },
                          textDecoration: 'none',
                          '.MuiTableCell-root': {
                            padding: '6px 16px',
                          },
                        }}
                      >
                        {expandableContent && (
                          <TableCell sx={{ width: 40, padding: '4px !important' }}>
                            <IconButton
                              size="small"
                              onClick={(e) => toggleExpand(rowKey, e)}
                              aria-label={expandedRows.has(rowKey) ? 'Collapse details' : 'Expand details'}
                              aria-expanded={expandedRows.has(rowKey)}
                            >
                              {expandedRows.has(rowKey)
                                ? <KeyboardArrowDownIcon />
                                : <KeyboardArrowRightIcon />}
                            </IconButton>
                          </TableCell>
                        )}
                        {bulkActionsEnabled && (
                          <TableCell>
                            <Checkbox
                              checked={selectedRows.includes(currentDisplayObject)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(_, checked) => {
                                if (checked) {
                                  setSelectedRows([...selectedRows, currentDisplayObject]);
                                } else {
                                  setSelectedRows(selectedRows.filter((r) => r !== currentDisplayObject));
                                }
                              }}
                            />
                          </TableCell>
                        )}

                        {columns.map((column, index) => {
                          if (!isColumnVisible(column)) {
                            return null;
                          }

                          return (
                            <TableCell
                              // eslint-disable-next-line react/no-array-index-key
                              key={`${currentDisplayObject[rowRenderKeyAttribute]} ${tableId} column ${index}`}
                              sx={{
                                backgroundColor: getColumnGroup(column).color,
                                whiteSpace: column.disableWrap ? 'nowrap' : 'normal',
                              }}
                            >
                              {column.renderOverride && column.renderOverride(getValue(column, currentDisplayObject), currentDisplayObject)}
                              {!column.renderOverride && (
                                <>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="textPrimary"
                                      sx={{
                                        ...(column.displayType === DisplayType.JSON) && { whiteSpace: 'pre-wrap' }
                                      }}
                                    >
                                      {(getFormattedValue(column, currentDisplayObject) as unknown) as any}
                                    </Typography>
                                    {column.copyToClipboard && (
                                      <CopyToClipboardButton text={getValueAsString(column, currentDisplayObject)} log={log} />
                                    )}
                                  </Box>
                                  {column.subTextAttribute && (
                                    <Typography variant="body2" color="textSecondary">
                                      {(getSubtextValue(column, currentDisplayObject) as unknown) as any}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </TableCell>
                          );
                        })}
                        {rowActions && <TableCell align="right">{rowActions(currentDisplayObject)}</TableCell>}
                      </TableRow>
                      {expandableContent && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + (bulkActionsEnabled ? 1 : 0) + (rowActions ? 1 : 0) + 1}
                            sx={{ padding: 0, borderBottom: expandedRows.has(rowKey) ? undefined : 'none' }}
                          >
                            <Collapse in={expandedRows.has(rowKey)} unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                {expandableContent(currentDisplayObject)}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              {!loading && displayObjects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Typography
                      sx={{
                        width: '100%',
                        textAlign: 'center',
                        p: 3,
                      }}
                      color="textSecondary"
                    >
                      {emptyText || 'There are no items to display'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {showEmptyLoadingState && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} data-testid="table-empty-loading">
                    <LoadingScreen sx={{ width: '75px' }} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
      {!disablePagination && (
        <TablePagination
          component="div"
          sx={{
            // One line, and scroll rather than wrap when the viewport genuinely cannot
            // hold it. MUI's toolbar wraps by default, which on a table narrower than its
            // own footer pushed the page numbers onto a second row.
            '& .MuiTablePagination-toolbar': { flexWrap: 'nowrap', overflowX: 'auto' },
            '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
              whiteSpace: 'nowrap',
            },
          }}
          count={serverPaging ? serverPaging.totalCount : fiteredData.length}
          onPageChange={(e, newPage) => (serverPaging ? serverPaging.onPageChange(newPage) : setPage(newPage))}
          onRowsPerPageChange={(e) =>
            serverPaging
              ? serverPaging.onRowsPerPageChange(parseInt(e.target.value, 10))
              : setLimit(parseInt(e.target.value, 10))
          }
          page={serverPaging ? serverPaging.page : page}
          rowsPerPage={serverPaging ? serverPaging.rowsPerPage : limit}
          // The server caps a page at 200; offering 1,000 in server mode would be an
          // option that always errors. A caller may cap it further still.
          rowsPerPageOptions={
            serverPaging ? serverPaging.rowsPerPageOptions ?? [20, 50, 100, 200] : [20, 50, 200, 1000, 5000]
          }
          // Page numbers, so a 444-page device list can be jumped into rather than walked.
          ActionsComponent={(actionProps) => (
            <TetherTablePaginationActions
              {...actionProps}
              // A page shorter than the page size is the last one. The only end-of-list
              // signal there is when the server did not report a total.
              //
              // Against the SERVER's page size in server mode, not this component's `limit`:
              // `limit` is the client-mode state and `onRowsPerPageChange` never writes to it
              // when `serverPaging` is set, so it sits at 20 forever while the footer's select
              // says 50. A short final page of 30 rows then failed `30 < 20`, the next arrow
              // stayed enabled, and the operator walked into an empty page.
              isLastPage={displayObjects.length < (serverPaging ? serverPaging.rowsPerPage : limit)}
            />
          )}
          labelDisplayedRows={({ from, to, count }) =>
            count < 0
              ? `${from.toLocaleString()}–${to.toLocaleString()} of more than ${to.toLocaleString()}`
              : `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`
          }
        />
      )}
    </Box>
  );
}

export default TetherTable;
